/**
 * Form pane (FEAT-2026-0005, Phase 2).
 *
 * Schema-driven form rendered with @rjsf/mui. The JSON Schema comes from the
 * backend (`GET /report-templates/:key/schema`) which converts the per-test
 * Zod schema into JSON Schema. RJSF's MUI theme automatically picks up the
 * app's MUI theme tokens, so the form looks native.
 *
 * Edits write back to the zustand store (which is what drives the live
 * preview pane next to it via debounce).
 */

import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { Alert, Box, CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import type { IChangeEvent } from '@rjsf/core';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import { useReportTemplateStore } from './useReportTemplateStore';
import { reportTemplateService } from '../../../services/reportTemplateService';
import { ObjectFieldTemplate } from './formTemplates/ObjectFieldTemplate';
import { FieldTemplate } from './formTemplates/FieldTemplate';
import { BaseInputTemplate } from './formTemplates/BaseInputTemplate';
import { ArrayFieldTemplate } from './formTemplates/ArrayFieldTemplate';
import { FormSearchContext } from './formTemplates/FormSearchContext';

// ── uiSchema (the Shared Header & Footer template is the sole editable one) ──
//
// The JSON Schema shipped by the backend is generated from Zod and only
// carries types + constraints — no friendly labels or help text. The uiSchema
// below layers on humanised labels, short help text, and visibility flags.
// The per-test template editor was retired (report-kit build, decision D1), so
// this is the ONLY uiSchema the reduced admin page needs — the former per-test
// map is gone.
const num = (label: string, help?: string): UiSchema => ({
  'ui:title': label,
  ...(help ? { 'ui:help': help } : {}),
});

const HEADER_FOOTER_UI_SCHEMA: UiSchema = {
  schemaVersion: { 'ui:widget': 'hidden' },

  header: {
    'ui:title': 'Header (logo, title, project & sample tables)',
    logoHeight:           num('Logo height (pt)'),
    logoTop:              num('Logo top (pt)', 'Y-position of the logo from the page top.'),
    titleFontSize:        num('Title font size (pt)'),
    titleLineHeight:      num('Title line-height (pt)', 'Vertical pitch when more than one title line is drawn.'),
    methodFontSize:       num('Method line font size (pt)'),
    titleColor:           num('Title colour (hex)'),
    dividerWidth:         num('Title divider width (pt)'),
    dividerColor:         num('Title divider colour (hex)'),
    infoHeadingFontSize:  num('Info-tables heading font size (pt)'),
    infoBodyFontSize:     num('Info-tables body font size (pt)'),
    infoRowHeight:        num('Info-tables row height (pt)'),
    infoLabelColumnWidth: num('Info-tables label column width (pt)'),
    infoTopGap:           num('Top gap above info heading (pt)'),
    infoBottomGap:        num('Bottom gap below info table (pt)'),
  },

  footer: {
    'ui:title': 'Footer (lab address, page number, signoffs)',
    reserveFromBottom:    num('Reserve from page bottom (pt)', 'Distance from the page bottom to the footer divider line.'),
    dividerColor:         num('Footer divider colour (hex)'),
    dividerWidth:         num('Footer divider width (pt)'),
    addressFontSize:      num('Lab-address font size (pt)'),
    pageNumberFontSize:   num('"Page X of N" font size (pt)'),
    signoffFontSize:      num('Tested / Reviewed / Approved font size (pt)'),
    noteFontSize:         num('Bottom note font size (pt)'),
    noteText:             num('Bottom note text', 'Use the {version} token to substitute the current AppVersion.'),
    textColor:            num('Footer text colour (hex)'),
  },
};

/**
 * Pick the uiSchema for a given test type. Only the Shared Header & Footer
 * template is admin-editable now; everything else falls through to default
 * RJSF rendering (an empty overrides object).
 */
const uiSchemaForTest = (testName: string | null | undefined): UiSchema =>
  testName === 'Shared Header & Footer' ? HEADER_FOOTER_UI_SCHEMA : {};

interface Props {
  templateKey: string;
  /** Test name (e.g. "Atterberg Limits") — drives uiSchema selection. */
  testName: string | null;
}

export const FormPane = ({ templateKey, testName }: Props) => {
  const draftConfig  = useReportTemplateStore((s) => s.draftConfig);
  const setDraftConfig = useReportTemplateStore((s) => s.setDraftConfig);
  const setActiveSection = useReportTemplateStore((s) => s.setActiveSection);

  const [schema, setSchema] = useState<RJSFSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lightweight client-side search. Lower-cased + trimmed and shared with
  // ObjectFieldTemplate (root) via context so accordions hide when nothing
  // inside them matches. No debounce — the work is a substring scan over
  // a small set of strings, runs on every keystroke without lag.
  const [searchInput, setSearchInput] = useState('');
  const searchState = useMemo(
    () => ({ query: searchInput.trim().toLowerCase() }),
    [searchInput],
  );

  /**
   * Walk a focused field's RJSF id back to the top-level config section.
   * RJSF ids look like `root_<top>_<sub>_<sub>…` so the second segment is
   * the top-level property name (e.g. "page", "plasticityChart"). Wired
   * into the form pane's onFocusCapture so the highlight overlay tracks
   * the user's CURRENT focus, not just the most-recently expanded
   * accordion. Schema bookkeeping fields ("schemaVersion") are ignored.
   */
  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Climb to the nearest element carrying a `data-rjsf-id` or `id`
    // matching the RJSF naming convention.
    let node: HTMLElement | null = target;
    while (node && node !== e.currentTarget) {
      const id = node.id || node.getAttribute('data-rjsf-id') || '';
      const m = id.match(/^root_([a-zA-Z0-9]+)/);
      if (m) {
        const section = m[1];
        if (section !== 'schemaVersion') setActiveSection(section);
        return;
      }
      node = node.parentElement;
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reportTemplateService.getSchema(templateKey)
      .then((s) => { if (!cancelled) setSchema(s as RJSFSchema); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [templateKey]);

  const onChange = (e: IChangeEvent<unknown>) => {
    setDraftConfig(e.formData);
  };

  // Custom templates make the form ~3× denser than the default MUI theme.
  // ObjectFieldTemplate renders the top level as collapsible Accordion sections;
  // FieldTemplate replaces RJSF's giant labels with caption-style ones; the
  // BaseInputTemplate uses size="small" inputs; the ArrayFieldTemplate
  // collapses arrays of primitives (xTickPositions, etc.) into a compact
  // horizontal flex row instead of one stacked input per item.
  const templates = useMemo(() => ({
    ObjectFieldTemplate,
    FieldTemplate,
    BaseInputTemplate,
    ArrayFieldTemplate,
  }), []);

  const uiSchema = useMemo(() => uiSchemaForTest(testName), [testName]);

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
      <CircularProgress size={24} />
    </Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!schema) return null;

  // RJSF v6 typings — cast to the ComponentProps shape.
  const formProps: ComponentProps<typeof Form> = {
    schema,
    uiSchema,
    formData: draftConfig,
    validator,
    onChange,
    templates,
    children: <></>,
    showErrorList: 'bottom',
    liveValidate: false,
  };

  return (
    <FormSearchContext.Provider value={searchState}>
      <Box
        onFocusCapture={handleFocus}
        sx={{
          height: '100%',
          display: 'flex', flexDirection: 'column', minHeight: 0,
        }}
      >
        {/* Sticky search bar above the scrollable form body. Filters which
            top-level accordions render — typing a query collapses the form
            down to "just the bits that contain X" so users don't have to
            expand-and-scroll through all 30+ fields. */}
        <Box sx={{
          flexShrink: 0,
          px: 2, py: 1, borderBottom: 1, borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}>
          <TextField
            fullWidth size="small"
            placeholder="Search fields and sections…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={() => setSearchInput('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
              sx: { fontSize: '0.85rem' },
            }}
          />
        </Box>
        <Box sx={{
          flex: 1, overflow: 'auto', minHeight: 0,
          px: 2, py: 1.5,
          // Tighten everything that RJSF puts inside its form-group divs so the
          // form feels like a settings panel, not a sign-up form.
          '& .form-group': { mb: 0 },
          '& fieldset': { borderRadius: 1, m: 0, p: 0, border: 'none' },
          '& .MuiFormHelperText-root': { fontSize: '0.7rem', mt: 0.25 },
          // Tighten array fields (e.g. tickPositions).
          '& .array-item': { mb: 0.5 },
        }}>
          <Form {...formProps} />
        </Box>
      </Box>
    </FormSearchContext.Provider>
  );
};
